import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { AddressType } from '@uplift/types';
import { useCallback, useMemo, useState } from 'react';
import { Alert } from '../../Alert/alert.components';
import { Button } from '../../Button';
import { Modal, ModalContent } from '../../Modal';
import { ConvertedAddressFormatType, AddressVerifySelect } from './AddressVerifySelect.component';
import { useFetchAddressVerifySuggestion } from './use-fetch-address-verify-suggestion';
import { useCurrentuser } from '@uplift/contexts';

export type AddressVerifyModalTypes = {
  onClose: () => void;
  open: boolean;
  addressVerify: AddressType;
  onAddressConfirm: (arg0: AddressType) => void;
};

export type AddressOptionType = 'original' | 'suggested';
export type AddressVerifyModalRawTypes = {
  onClose: () => void;
  open: boolean;
  convertedVerifyAddress: ConvertedAddressFormatType;
  convertedSuggestionAddress: ConvertedAddressFormatType | null;
  onAddressConfirm: (option: AddressOptionType) => void;
  view: 'suggestion' | 'error';
};


export const AddressVerifyModalRaw:
React.FC<AddressVerifyModalRawTypes> = ({ open, onClose, onAddressConfirm, convertedVerifyAddress, convertedSuggestionAddress, view }) => {
  const isSmall = useIsMobile('sm');
  const { classes: modalClasses } = useModalStyles();
  const { classes } = useStyles();
  const [ checked, setChecked ] = useState<AddressOptionType>('suggested');
  const me = useCurrentuser();
  const l10n = useL10n();

  const closeModalHandler = () => {
    onClose();
  };

  const checkChangeHandler = (value: AddressOptionType) => {
    setChecked(value);
  };

  const confirmAddressHandler = () => {
    onAddressConfirm(checked);
  };

  const title: string = view === 'suggestion' ? 'Verify your address' : 'Address not recognized';
  const alertIconProps = !isSmall ? {} : { icon: false };

  return (
    <Modal
      title={title}
      onClose={closeModalHandler}
      open={open}
      maxWidth="md"
      classes={modalClasses}
      showCloseIcon={view !== 'error'}
    >
      <ModalContent>
        <Box mt={-3} mb={3}>
          {view === 'error' && me.role === 'patient' ? (
            <Alert color="warning" severity="warning" {...alertIconProps}>
              <Typography variant="body2" color="textPrimary">{l10n.accountSettings.insurance.addressVerifyAlert}</Typography>
            </Alert>
          ) : view === 'error' && me.role === 'superadmin' && (
            <Alert color="warning" severity="warning" {...alertIconProps}>
              <Typography variant="body2" color="textPrimary">{l10n.accountSettings.insurance.adminAddressVerifyAlert}</Typography>
            </Alert>
          )}
          {view === 'suggestion' && <Typography variant="body2" color="textPrimary">{l10n.accountSettings.insurance.addressFormattingSuggestion}</Typography>}
        </Box>
        {view === 'error' && (
          <>
            <Box mb={1}>
              <Typography variant="subtitle2">Original Address</Typography>
            </Box>
            <Typography>{convertedVerifyAddress.address1}</Typography>
            <Typography>{convertedVerifyAddress.address2}</Typography>
            <Typography>{convertedVerifyAddress.addressCityState}</Typography>
          </>
        )}
        {view === 'suggestion' && (
          <AddressVerifySelect
            verifyAddress={convertedVerifyAddress}
            suggestionAddress={convertedSuggestionAddress}
            checked={checked}
            onChange={checkChangeHandler}
          />
        )}
      </ModalContent>
      <Box className={classes.modalActions}>
        {view === 'suggestion' && (
          <Button
            variant="contained"
            color="secondary"
            onClick={confirmAddressHandler}
            fullWidth={isSmall}
          >Choose This Address</Button>
        )}
        {view === 'error' && (
          <>
            <Button
              color="secondary"
              onClick={confirmAddressHandler}
              fullWidth={isSmall}
            >Continue With This Address</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={closeModalHandler}
              fullWidth={isSmall}
            >Edit Address</Button>
          </>
        )}
      </Box>
    </Modal>
  );
};



const useStyles = makeStyles()((theme) => ({
  desc: {
    color: theme.palette.text.primary
  },
  modalActions: {
    padding: theme.spacing(2,3),
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      alignItems: 'initial',
      flexDirection: 'column-reverse',
    }
  }
}));

const useModalStyles = makeStyles()({
  paperWidthMd: {
    width: '600px',
    maxWidth: '600px'
  },
});



export const AddressVerifyModal:
React.FC<AddressVerifyModalTypes> = ({ addressVerify, open, onAddressConfirm, ...props }) => {
  const constraint = useMemo(() => open, [ open ]);
  const { suggestion, loading } = useFetchAddressVerifySuggestion({
    onAddressConfirm,
    addressVerify,
    constraint: constraint
  });

  const convertAddress = useCallback((address: AddressType): ConvertedAddressFormatType => {
    return {
      address1: address['address1'],
      address2: address['address2'] ?? '',
      addressCityState: [ address['city'], address['state'], address['zip'] ].filter(x => x).join(' ')
    };
  }, []);

  const confirmAddressHandler = useCallback((option: AddressOptionType) => {
    const isOriginal = !suggestion || option === 'original';

    const address = isOriginal ? addressVerify : suggestion?.address;

    onAddressConfirm(address as AddressType);
  }, [ addressVerify, onAddressConfirm, suggestion ]);

  if (loading) return null;

  const view: AddressVerifyModalRawTypes['view'] = !suggestion ? 'error' : 'suggestion';

  return (
    <AddressVerifyModalRaw
      onAddressConfirm={confirmAddressHandler}
      view={view}
      open={open}
      convertedSuggestionAddress={suggestion ? convertAddress(suggestion.address) : null}
      convertedVerifyAddress={convertAddress(addressVerify)}
      {...props}
    />
  );
};
